import { Box, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { sortAndFilter } from 'components/tables/AgGridTable';
import { formatDate } from 'utilities/common';
import { formatTimestampToDate24Hours } from 'utilities/common';
import AngleRightIcon from 'common/customIcons/angle_right';
import { fetchWalletLogs } from '../../redux/slices/member';
import SearchIcon from 'common/customIcons/search';
import { CloseRounded } from '@mui/icons-material';
import { useDebounce } from 'hooks/useDebounce';

export default function WalletLogs() {
    const dispatch = useDispatch();
    const { logsloading: loading, walletlogs = {} } = useSelector(state => state.members);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const gridRefApi = useRef(null);
    const pageRef = useRef(0);
    const navigate = useNavigate()
    const { memberId } = useParams();
    const [searchTerm, setSearchTerm] = useState("");

    const columnDefs = [
        {
            headerName: 'Wallet Adjustment reason ',
            field: 'newChanges',
            suppressMenu: false,
            key: 'newChanges',
            minWidth: 250,
            cellRenderer: useCallback((props) => (
                props?.value ?
                    <Tooltip title={props?.value?.walletAdjustmentReason} arrow>
                        <span>
                            {props?.value?.walletAdjustmentReason?.slice(0, 50)}
                            {props?.value?.walletAdjustmentReason?.length > 50 ? '...' : ''}
                        </span>
                    </Tooltip>
                    : '-'
            ), []),
            sortable: false,

        },
        {
            headerName: 'Date/Time',
            field: 'createdDate',
            minWidth: 150,
            cellRenderer: useCallback((props) => (
                props?.value ? formatTimestampToDate24Hours(props?.value) : '-'
            ), []),
            flex: 1.3,
            sortable: false
        },
        {
            headerName: 'Wallet Type',
            field: 'previousChanges',
            minWidth: 150,
            cellRenderer: (props) => {
                return props?.value?.walletType ? `${props?.value?.walletType}` : '-';
            },
            flex: 1.3,
            sortable: false
        },
        {
            headerName: 'Updated By',
            field: 'updatedBy',
            key: 'fullName',
            suppressMenu: false,
            minWidth: 150,
            sortable: false
        },
        {
            headerName: 'Wallet Amount (Previous Changes)',
            field: 'previousChanges',
            suppressMenu: false,
            cellRenderer: (props) => {
                return props?.value?.walletAmount ? `$${Number(props?.value?.walletAmount).toFixed(2)}` : '-';
            },
            minWidth: 220,
            sortable: false,



        },
        {
            headerName: 'Wallet Amount (New Changes)',
            field: 'newChanges',
            suppressMenu: false,
            cellRenderer: (props) => {
                return props?.value?.walletAmount ? `$${Number(props?.value?.walletAmount).toFixed(2)}` : '-';
            },
            minWidth: 220,
            headerClass: 'left-aligned-cell-header',
            sortable: false,
            headerClass: 'right-aligned-cell-header',
            cellStyle: { textAlign: 'right', justifyContent: 'end' },
        },


    ];

    const getRows = async (params) => {
        gridRefApi.current = params;
        dispatch(fetchWalletLogs({ skip: pageRef.current, limit: 20, searchTerm: searchTermRef.current, memberId: memberId })).unwrap().then(res => {
            const { logs, total } = res;

            params.successCallback([...logs], total);
            pageRef.current = pageRef.current + 20;
        }).catch(() => { params.successCallback([], 0); });
    };

    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
        // Debounce the search callback
    };

    const handleSearch = useDebounce(() => {
        pageRef.current = 0;
        searchFn();
    }, 500);

    const searchFn = () => {
        const dataSource = {
            getRows,
        };
        if (mainGridApiRef.current) {
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption("datasource", dataSource);
        }
    };
    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (walletlogs?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [walletlogs, loading]);

    return (
        <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }}>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="stretch" gap={2} mb={2}>
                <Box display="flex" gap={1} alignItems="center" color='text.main' lineHeight={1}>
                    <span style={{ cursor: "pointer" }} onClick={() => navigate(-1)} ><AngleRightIcon fontSize="14px" />
                    </span>
                    <Typography variant='h4'>Wallet Logs</Typography>
                </Box>
                <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                    <TextField
                        className="custom-search-field"
                        placeholder="Search"
                        label=""
                        value={searchTerm}
                        onChange={(e) => searchHandler(e.target.value)}
                        sx={{ minWidth: "280px" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton size="small">
                                        <SearchIcon width="18px" height="18px" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment:
                                searchTerm?.length > 0 ? (
                                    <InputAdornment>
                                        <IconButton onClick={() => searchHandler("")} size="small">
                                            <CloseRounded />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                        }}
                    />
                </Stack>
            </Stack>
            <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
        </Box>
    );
}